import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import {
	HubCommentModel,
	HubCommentPagingInformationModel,
	HubCommentSaveModel,
	HubCommentStatisticsModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import { SortDirection } from "../../common";

export function getCommentsList(
	announcementId: number,
	amount: number | null,
	afterComment: HubCommentPagingInformationModel | null,
	sortDirection: SortDirection,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubCommentModel>> {
	return httpClient.get<LazyLoadPageResult<HubCommentModel>>(
		`/UiApi/Announcements/${announcementId}/Comments`,
		{
			amount,
			afterComment,
			sortDirection
		},
		{ signal }
	);
}

export function getRelatedCommentThread(
	announcementId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly HubCommentModel[]> {
	return httpClient.get<readonly HubCommentModel[]>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Thread`,
		undefined,
		{ signal }
	);
}

export function registerCommentReaction(
	announcementId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.post<HubCommentStatisticsModel>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function updateCommentReaction(
	announcementId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.put<HubCommentStatisticsModel>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function unregisterCommentReaction(
	announcementId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.delete<HubCommentStatisticsModel>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Reactions`,
		undefined,
		{ signal }
	);
}

export function getCommentReactionCounts(
	announcementId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return httpClient.get<readonly ReactionCounterModel[]>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Reactions/Counts`,
		undefined,
		{signal}
	);
}

export function getCommentReactions(
	announcementId: number,
	commentId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{signal}
	);
}

export function createComment(
	announcementId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Announcements/${announcementId}/Comments/`,
		newComment,
		{ signal }
	);
}

export function editComment(
	announcementId: number,
	commentId: number,
	updatedComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.put<HubCommentModel>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}`,
		updatedComment,
		{ signal }
	);
}

export function deleteComment(
	announcementId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`/UiApi/Announcements/${announcementId}/Comments/${commentId}`,
		undefined,
		{ signal }
	);
}

export function createReply(
	announcementId: number,
	parentCommentId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Announcements/${announcementId}/Comments/${parentCommentId}/Replies`,
		newComment,
		{ signal }
	);
}
